import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { useErrorBoundary, useLocalize } from '@treams/treams-hooks';
import { CloseIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { IconButton } from '@treams/treams-ui';

const AnnouncementBanner = () => {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const [, attempt] = useErrorBoundary();

	const { data } = graphql.useAnnouncementsQuery({
		variables: {
			displayType: graphql.AnnouncementDisplayType.Banner,
			first: 10,
		},
	});

	const [markAsRead, { loading: marking }] = graphql.useMarkAnnouncementAsReadMutation();

	const announcement = data?.announcements?.edges
		?.map(({ node }) => node)
		.find((node) => node.enabled && !node.isRead);

	const announcementID = announcement?.id;

	const handleDismiss = useCallback(
		() =>
			attempt(async () => {
				if (!announcementID) return;

				await markAsRead({
					variables: {
						input: { announcementID },
					},
				});
			})(),
		[announcementID, attempt, markAsRead],
	);

	if (!announcement) {
		return null;
	}

	return (
		<Banner>
			<BannerContent>
				<BannerText>{localize(announcement.title).text}</BannerText>

				<BannerButtons>
					<span
						title={t('announcements.banner.dismiss')}
						aria-label={t('announcements.banner.dismiss')}
					>
						<BannerDismissButton
							small
							type="plain"
							icon={<CloseIcon />}
							onClick={handleDismiss}
							loading={marking}
						/>
					</span>
				</BannerButtons>
			</BannerContent>
		</Banner>
	);
};

const Banner = styled.div`
	background-image: ${({ theme }) => theme.images.lineGradient};
`;

const BannerContent = styled.div`
	display: flex;
	flex-direction: row;

	height: 55px;
	width: 100%;

	padding: 10px;
	padding-left: 60px;

	align-items: center;
`;

const BannerText = styled.p`
	color: white;
`;

const BannerButtons = styled.div`
	display: flex;
	flex-direction: row;

	margin-left: auto;
	padding-right: 60px;

	gap: 5px;
`;

const BannerDismissButton = styled(IconButton)`
	${({ theme }) => css`
		background-color: transparent;

		svg path {
			fill: white;
		}

		&:hover {
			background-color: ${theme.colors.WhiteTransparantMediumSoft};
		}
	`}
`;

export default AnnouncementBanner;
